import { useQuery } from "@tanstack/react-query";
import {
  Rocket,
  CheckCircle,
  XCircle,
  ExternalLink,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  deploymentsApi,
  deploymentRequestsApi,
  Deployment,
  DeploymentComponent,
  DeploymentRequest,
} from "@/api/client";
import { titleCase } from "@/lib/utils";

interface DeploymentDetailsModalProps {
  deploymentId: string;
  onClose: () => void;
}

export default function DeploymentDetailsModal({
  deploymentId,
  onClose,
}: DeploymentDetailsModalProps) {
  // Fetch deployment details
  const { data: deploymentData, isLoading: deploymentLoading } = useQuery({
    queryKey: ["deployment", deploymentId],
    queryFn: () => deploymentsApi.get(deploymentId),
    enabled: !!deploymentId,
  });

  const deployment = deploymentData?.deployment;
  const components = deploymentData?.components;

  // Fetch linked deployment request if exists
  const { data: requestData } = useQuery({
    queryKey: ["deployment-request", deployment?.approval_request_id],
    queryFn: () => deploymentRequestsApi.get(deployment!.approval_request_id!),
    enabled: !!deployment?.approval_request_id,
  });

  const deploymentRequest = requestData?.deployment_request;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {deploymentLoading ? (
          <div className="p-6 text-center text-gray-500">
            Loading deployment details...
          </div>
        ) : !deployment ? (
          <div className="p-6 text-center text-gray-500">
            Deployment not found
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Rocket className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Deployment Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      <span>{titleCase(deployment.service_name)}</span> &bull;{" "}
                      {deployment.environment_name}
                    </p>
                  </div>
                </div>
                <StatusBadge status={deployment.status} />
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Version and Deployment Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoCard label="Version" value={deployment.version} mono />
                <InfoCard
                  label="Previous Version"
                  value={deployment.previous_version || "-"}
                  mono
                />
                <InfoCard
                  label="Deployed At"
                  value={formatDate(deployment.deployed_at)}
                />
                <InfoCard label="Deployed By" value={deployment.deployed_by} />
                <InfoCard
                  label="Status"
                  value={<StatusIndicator status={deployment.status} />}
                />
                <InfoCard
                  label="Method"
                  value={
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        deployment.deployment_method === "ci_cd"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {deployment.deployment_method === "ci_cd"
                        ? "CI/CD"
                        : "Manual"}
                    </span>
                  }
                />
                {deployment.group_name && (
                  <InfoCard
                    label="Group"
                    value={
                      <Link
                        to={`/groups/${deployment.group_slug}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deployment.group_name}
                      </Link>
                    }
                  />
                )}
              </div>

              {/* CI/CD Job Link */}
              {deployment.ci_cd_job_url && (
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    CI/CD Job
                  </p>
                  <a
                    href={deployment.ci_cd_job_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-4 w-4" />
                    {deployment.ci_cd_job_id || "View Job"}
                  </a>
                </div>
              )}

              {/* Notes */}
              {deployment.notes && (
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Notes
                  </p>
                  <p className="text-sm text-gray-900">{deployment.notes}</p>
                </div>
              )}

              {/* Deployment Request Section */}
              {deploymentRequest ? (
                <DeploymentRequestSection request={deploymentRequest} />
              ) : (
                <NoApprovalRequiredSection />
              )}

              {/* Components Updated */}
              <ComponentsSection
                components={components}
                deployment={deployment}
              />

              {/* Footer Link */}
              <div className="pt-4 border-t border-gray-200">
                <Link
                  to={`/deployments?id=${deployment.id}`}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4" />
                  View in deployments list
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Helper Components

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<
    string,
    { bg: string; text: string; icon: React.ReactNode; label: string }
  > = {
    success: {
      bg: "bg-green-100",
      text: "text-green-800",
      icon: <CheckCircle className="h-3.5 w-3.5 mr-1" />,
      label: "Success",
    },
    completed: {
      bg: "bg-green-100",
      text: "text-green-800",
      icon: <CheckCircle className="h-3.5 w-3.5 mr-1" />,
      label: "Completed",
    },
    failed: {
      bg: "bg-red-100",
      text: "text-red-800",
      icon: <XCircle className="h-3.5 w-3.5 mr-1" />,
      label: "Failed",
    },
    draft: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: <FileText className="h-3.5 w-3.5 mr-1" />,
      label: "Draft",
    },
    pending_approval: {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      icon: <FileText className="h-3.5 w-3.5 mr-1" />,
      label: "Pending Approval",
    },
    approved: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      icon: <CheckCircle className="h-3.5 w-3.5 mr-1" />,
      label: "Approved",
    },
    deploying: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      icon: <Rocket className="h-3.5 w-3.5 mr-1" />,
      label: "Deploying",
    },
    cancelled: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: <XCircle className="h-3.5 w-3.5 mr-1" />,
      label: "Cancelled",
    },
  };

  const config = statusConfig[status] || {
    bg: "bg-gray-100",
    text: "text-gray-800",
    icon: null,
    label: status,
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function StatusIndicator({ status }: { status: string }) {
  // Determine icon and color based on status
  const getStatusDisplay = () => {
    switch (status) {
      case "success":
      case "completed":
      case "approved":
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-500" />,
          label: titleCase(status.replace(/_/g, " ")),
        };
      case "failed":
      case "rejected":
      case "cancelled":
        return {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
          label: titleCase(status.replace(/_/g, " ")),
        };
      case "deploying":
        return {
          icon: <Rocket className="h-4 w-4 text-blue-500" />,
          label: "Deploying",
        };
      case "pending_approval":
      case "draft":
      default:
        return {
          icon: <FileText className="h-4 w-4 text-gray-500" />,
          label: titleCase(status.replace(/_/g, " ")),
        };
    }
  };

  const { icon, label } = getStatusDisplay();

  return (
    <span className="flex items-center gap-1">
      {icon}
      <span>{label}</span>
    </span>
  );
}

function InfoCard({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p
        className={`mt-1 font-medium text-gray-900 ${mono ? "font-mono text-sm" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}

function DeploymentRequestSection({ request }: { request: DeploymentRequest }) {
  // Get approval summary
  const approvals = request.required_approvals || [];
  const approvedCount = approvals.filter((a) => a.status === "approved").length;

  return (
    <div className="border-t pt-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-medium text-gray-900">
          Deployment Request
        </h3>
      </div>
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">
            Requested by{" "}
            <span className="font-medium text-gray-900">
              {request.requested_by_name || "Unknown"}
            </span>{" "}
            on {formatDate(request.requested_at)}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              request.status === "completed"
                ? "bg-green-100 text-green-800"
                : request.status === "approved"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800"
            }`}
          >
            {request.status}
          </span>
        </div>
        {approvals.length > 0 && (
          <div className="text-sm text-gray-600">
            {approvedCount === approvals.length ? (
              <span className="flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                All {approvals.length} approval{approvals.length > 1 ? "s" : ""}{" "}
                received
              </span>
            ) : (
              <span>
                {approvedCount} of {approvals.length} approvals received
              </span>
            )}
          </div>
        )}
        <Link
          to={`/deployment-requests?id=${request.id}`}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View Request Details
        </Link>
      </div>
    </div>
  );
}

function NoApprovalRequiredSection() {
  return (
    <div className="border-t pt-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-medium text-gray-900">
          Deployment Request
        </h3>
      </div>
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600">
          No approval request — this environment does not require deployment
          approval.
        </p>
      </div>
    </div>
  );
}

function ComponentsSection({
  components,
  deployment,
}: {
  components: DeploymentComponent[] | undefined;
  deployment: Deployment;
}) {
  // Try to get components from the API response first
  let displayComponents = components;

  // Fallback to metadata if no components from API
  if (!displayComponents || displayComponents.length === 0) {
    const metadata = deployment.metadata || {};
    const componentVersions = metadata.component_versions as
      | Record<string, string>
      | undefined;
    if (componentVersions && Object.keys(componentVersions).length > 0) {
      displayComponents = Object.entries(componentVersions).map(
        ([name, version]) => ({
          component_name: name,
          component_identifier: name,
          new_version: version,
        }),
      );
    }
  }

  if (!displayComponents || displayComponents.length === 0) {
    return null;
  }

  return (
    <div className="border-t pt-4">
      <h3 className="text-sm font-medium text-gray-900 mb-3">
        Components Updated ({displayComponents.length})
      </h3>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Component
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Previous Version
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                New Version
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayComponents.map((comp, idx) => (
              <tr key={idx}>
                <td className="px-4 py-2">
                  <div className="text-sm font-medium text-gray-900">
                    {comp.component_name}
                  </div>
                  {comp.component_identifier !== comp.component_name && (
                    <div className="text-xs text-gray-500">
                      {comp.component_identifier}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-500 font-mono">
                  {comp.old_version || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 font-mono">
                  {comp.new_version}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Utility function
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
